import { useId } from "react";
import { PublicIcon, PublicIconName } from "./PublicIcon";

export type StatusKind = "ok" | "wait" | "lock" | "cal" | "none" | "lost" | "mail";
const SYMBOL: Record<Exclude<StatusKind, "lost">, PublicIconName> = { ok: "check", wait: "clock", lock: "lock", cal: "calendar", none: "compass", mail: "mailcheck" };
const LIT: StatusKind[] = ["ok", "wait", "mail"];

/**
 * Status screens (boards 340-358, owner item 6): the route line ending in a stop. Lit blue with an icon when the news is good or pending,
 * a quiet outline for "no workspace / inactive / package not active", a broken dashed road ending in "?" for Page not found.
 * Replaces the old "registration successful" and "account" pictures. Decorative.
 */
export const StatusArt = ({ kind = "ok", width = 260, height = 170 }: { kind?: StatusKind; width?: number; height?: number }) => {
  const uid = useId().replace(/:/g, "");
  const w = width, h = height, cx = w * 0.78, cy = h * 0.36;
  const path = `M18 ${h - 22} C ${w * 0.28} ${h - 6} ${w * 0.22} ${h * 0.48} ${w * 0.46} ${h * 0.52} S ${w * 0.62} ${h * 0.3} ${cx} ${cy}`;
  const lit = LIT.includes(kind);
  const grid: JSX.Element[] = [];
  for (let x = 30; x < w + 40; x += 46) grid.push(<path key={`v${x}`} d={`M${x} 0 L${x - 40} ${h}`} stroke="var(--cy-divider)" strokeWidth="1.4" />);
  for (let y = 24; y < h + 20; y += 38) grid.push(<path key={`h${y}`} d={`M0 ${y} L${w} ${y - 18}`} stroke="var(--cy-divider)" strokeWidth="1.4" />);
  return (
    <svg className="cy-status-art" width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true" focusable="false" style={{ display: "block", flex: "none", maxInlineSize: "100%" }} data-kind={kind}>
      <defs>
        <linearGradient id={`${uid}g`} x1="0" y1="1" x2="1" y2="0"><stop offset="0" stopColor="var(--cy-primary)" /><stop offset=".55" stopColor="var(--cy-deep-blue)" /><stop offset="1" stopColor="var(--cy-brand-teal)" /></linearGradient>
        <radialGradient id={`${uid}m`} cx=".55" cy=".45" r=".6"><stop offset=".4" stopColor="#fff" /><stop offset="1" stopColor="#fff" stopOpacity="0" /></radialGradient>
        <mask id={`${uid}k`}><rect width={w} height={h} fill={`url(#${uid}m)`} /></mask>
      </defs>
      <g mask={`url(#${uid}k)`}>{grid}</g>
      {kind === "lost" ? (
        <>
          <path d={`M18 ${h - 22} C ${w * 0.28} ${h - 6} ${w * 0.22} ${h * 0.48} ${w * 0.46} ${h * 0.52}`} fill="none" stroke={`url(#${uid}g)`} strokeWidth="6" strokeLinecap="round" />
          <path d={`M${w * 0.46} ${h * 0.52} S ${w * 0.62} ${h * 0.3} ${cx} ${cy}`} fill="none" stroke="var(--cy-info-border)" strokeWidth="5" strokeLinecap="round" strokeDasharray="2 12" />
          <circle cx={cx} cy={cy} r="26" fill="var(--cy-surface)" stroke="var(--cy-control-border)" strokeWidth="2.5" strokeDasharray="5 5" />
          <text x={cx} y={cy + 9} textAnchor="middle" fontFamily="Poppins, Inter, sans-serif" fontSize="26" fontWeight="700" fill="var(--cy-muted)">?</text>
        </>
      ) : (
        <>
          <path d={path} fill="none" stroke={`url(#${uid}g)`} strokeWidth="6" strokeLinecap="round" />
          {lit ? (
            <>
              <circle cx={cx} cy={cy} r="44" fill="var(--cy-primary)" opacity=".08" /><circle cx={cx} cy={cy} r="34" fill="var(--cy-primary)" opacity=".12" />
              <circle cx={cx} cy={cy} r="25" fill="var(--cy-primary)" stroke="var(--cy-surface)" strokeWidth="4" />
            </>
          ) : (
            <>
              <circle cx={cx} cy={cy} r="38" fill="var(--cy-heading)" opacity=".06" />
              <circle cx={cx} cy={cy} r="25" fill="var(--cy-surface)" stroke="var(--cy-heading)" strokeWidth="4" />
            </>
          )}
          <g transform={`translate(${cx - 12} ${cy - 12})`} style={{ color: lit ? "var(--cy-surface)" : "var(--cy-primary)" }}><PublicIcon name={SYMBOL[kind]} size={24} strokeWidth={2.4} /></g>
        </>
      )}
      {[[18, h - 22], [w * 0.46, h * 0.52]].map(([x, y]) => <circle key={x} cx={x} cy={y} r="7" fill="var(--cy-surface)" stroke="var(--cy-primary)" strokeWidth="3" />)}
    </svg>
  );
};
